import React from 'react';
import { Table } from '@mantine/core';
import { SectionCard } from '../../shared/SectionCard/SectionCard';
import { asCurrency, sumDonePaymentsByMethod } from '../../shared/utils/dashboard-page.utils';
import { PaymentByMethod, SectionKey } from '../../shared/types/dashboard-page.types';
import { AdminPaymentlineView } from '../../../../services/admin-payments/admin-payments.service';
import { AdminSessionAuditView } from '../../../../services/admin-sessions/admin-sessions.service';
import type { ReportView } from '../../../../services/reports/reports.service';
import classes from './payment-method-section.module.scss';

export type PaymentMethodSectionProps = {
  collapsedSections: Record<SectionKey, boolean>;
  toggleSection: (section: SectionKey) => void;
  paymentByMethod: PaymentByMethod;
  activeSessions: AdminSessionAuditView[];
  payments: AdminPaymentlineView[];
  reportsBySessionId?: Record<number, ReportView>;
};

type SessionPaymentTotals = {
  sessionId: number;
  sessionName: string;
  openTime: string;
  credit: number;
  cash: number;
  tenbis: number;
  houseAccount: number;
  tipsCredit: number;
  tipsCash: number;
  tipsTenbis: number;
  tipsHouseAccount: number;
  cashMoves: number;
  openingCash: number;
};

// Manual drawer in/out changes what is physically there, so it belongs in the deposit figure.
const cashMovesNetOf = (report: ReportView | undefined): number =>
  (report?.drawers ?? []).reduce((sum, drawer) => sum + drawer.cashInTotal - drawer.cashOutTotal, 0);

const formatSessionTime = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  const pad = (part: number): string => String(part).padStart(2, '0');
  const weekday = date.toLocaleDateString('he-IL', { weekday: 'long' }).replace(/^יום /, '');
  return `${weekday} ${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${String(date.getFullYear()).slice(-2)} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const computeSessionTotals = (
  session: AdminSessionAuditView,
  payments: AdminPaymentlineView[],
  report: ReportView | undefined,
): SessionPaymentTotals => {
  const rows = payments.filter((payment) => payment.sessionId === session.sessionId);
  const cash = sumDonePaymentsByMethod(rows, 'cash', 'baseAmount');
  const credit = sumDonePaymentsByMethod(rows, 'credit', 'baseAmount');
  const tenbis = sumDonePaymentsByMethod(rows, 'tenbis', 'baseAmount');
  const houseAccount = sumDonePaymentsByMethod(rows, 'house_account', 'baseAmount');
  const tipsCash = report?.tipsCash ?? 0;
  const tipsCredit = report?.tipsCredit ?? 0;
  const tipsTenbis = report?.tipsTenbis ?? 0;
  const tipsHouseAccount = report?.tipsHouseAccount ?? 0;
  return {
    sessionId: session.sessionId,
    sessionName: session.sessionName,
    openTime: session.openTime,
    cash, credit, tenbis, houseAccount,
    tipsCash, tipsCredit, tipsTenbis, tipsHouseAccount,
    cashMoves: cashMovesNetOf(report),
    openingCash: report?.openingCash ?? 0,
  };
};

export const PaymentMethodSection: React.FC<PaymentMethodSectionProps> = ({
  collapsedSections,
  toggleSection,
  paymentByMethod,
  activeSessions,
  payments,
  reportsBySessionId = {},
}) => {
  const isMultiSession = activeSessions.length > 1;

  const sessionTotals = isMultiSession
    ? activeSessions.map((session) => computeSessionTotals(session, payments, reportsBySessionId[session.sessionId]))
    : [];
  const totals = sessionTotals.reduce(
    (acc, row) => ({
      cash: acc.cash + row.cash,
      credit: acc.credit + row.credit,
      tenbis: acc.tenbis + row.tenbis,
      houseAccount: acc.houseAccount + row.houseAccount,
      tipsCash: acc.tipsCash + row.tipsCash,
      tipsCredit: acc.tipsCredit + row.tipsCredit,
      tipsTenbis: acc.tipsTenbis + row.tipsTenbis,
      tipsHouseAccount: acc.tipsHouseAccount + row.tipsHouseAccount,
      cashMoves: acc.cashMoves + row.cashMoves,
      openingCash: acc.openingCash + row.openingCash,
    }),
    {
      cash: 0, credit: 0, tenbis: 0, houseAccount: 0,
      tipsCash: 0, tipsCredit: 0, tipsTenbis: 0, tipsHouseAccount: 0,
      cashMoves: 0, openingCash: 0,
    },
  );
  const singleSessionReport = reportsBySessionId[activeSessions[0]?.sessionId ?? -1];
  const singleSessionCashMoves = isMultiSession ? 0 : cashMovesNetOf(singleSessionReport);
  const singleSessionOpeningCash = isMultiSession ? 0 : (singleSessionReport?.openingCash ?? 0);
  // A zero net means nothing was moved, so the column/row is noise.
  const showCashMoves = isMultiSession ? totals.cashMoves !== 0 : singleSessionCashMoves !== 0;

  return (
    <SectionCard title="מכירות וטיפ לפי אמצעי תשלום" sectionKey="paymentMethod" collapsed={collapsedSections.paymentMethod} onToggle={() => toggleSection('paymentMethod')}>
      {isMultiSession ? (
        <div className={classes.tableScroll}>
          <Table className={`${classes.table} ${classes.tableScrollable}`}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th></Table.Th>
                <Table.Th>מזומן להפקדה</Table.Th>
                <Table.Th>פתיחת קופה</Table.Th>
                {showCashMoves && <Table.Th>תנועות מזומן</Table.Th>}
                <Table.Th>סה"כ טיפ</Table.Th>
                <Table.Th>טיפ מזומן</Table.Th>
                <Table.Th>טיפ אשראי</Table.Th>
                <Table.Th>סה"כ מכירות</Table.Th>
                <Table.Th>מכירות מזומן</Table.Th>
                <Table.Th>מכירות אשראי</Table.Th>
                <Table.Th>מכירות תן ביס</Table.Th>
                <Table.Th>מכירות חשבון בית</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sessionTotals.map((row) => (
                <Table.Tr key={row.sessionId}>
                  <Table.Th className={classes.sessionCell}>
                    {row.sessionName}
                    <span className={classes.sessionTime}>{formatSessionTime(row.openTime)}</span>
                  </Table.Th>
                  <Table.Td>{asCurrency(row.cash - row.tipsCredit - row.tipsTenbis - row.tipsHouseAccount + row.cashMoves)}</Table.Td>
                  <Table.Td>{asCurrency(row.openingCash)}</Table.Td>
                  {showCashMoves && <Table.Td>{asCurrency(row.cashMoves)}</Table.Td>}
                  <Table.Td>{asCurrency(row.tipsCash + row.tipsCredit + row.tipsTenbis + row.tipsHouseAccount)}</Table.Td>
                  <Table.Td>{asCurrency(row.tipsCash)}</Table.Td>
                  <Table.Td>{asCurrency(row.tipsCredit)}</Table.Td>
                  <Table.Td>{asCurrency(row.cash + row.credit + row.tenbis + row.houseAccount)}</Table.Td>
                  <Table.Td>{asCurrency(row.cash)}</Table.Td>
                  <Table.Td>{asCurrency(row.credit)}</Table.Td>
                  <Table.Td>{asCurrency(row.tenbis)}</Table.Td>
                  <Table.Td>{asCurrency(row.houseAccount)}</Table.Td>
                </Table.Tr>
              ))}
              <Table.Tr className={classes.totalRow}>
                <Table.Th className={classes.sessionCell}>סה"כ</Table.Th>
                <Table.Td>{asCurrency(totals.cash - totals.tipsCredit - totals.tipsTenbis - totals.tipsHouseAccount + totals.cashMoves)}</Table.Td>
                <Table.Td>{asCurrency(totals.openingCash)}</Table.Td>
                {showCashMoves && <Table.Td>{asCurrency(totals.cashMoves)}</Table.Td>}
                <Table.Td>{asCurrency(totals.tipsCash + totals.tipsCredit + totals.tipsTenbis + totals.tipsHouseAccount)}</Table.Td>
                <Table.Td>{asCurrency(totals.tipsCash)}</Table.Td>
                <Table.Td>{asCurrency(totals.tipsCredit)}</Table.Td>
                <Table.Td>{asCurrency(totals.cash + totals.credit + totals.tenbis + totals.houseAccount)}</Table.Td>
                <Table.Td>{asCurrency(totals.cash)}</Table.Td>
                <Table.Td>{asCurrency(totals.credit)}</Table.Td>
                <Table.Td>{asCurrency(totals.tenbis)}</Table.Td>
                <Table.Td>{asCurrency(totals.houseAccount)}</Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </div>
      ) : (
        <Table className={classes.table}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th></Table.Th>
              <Table.Th>סכום</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            <Table.Tr>
              <Table.Th className={classes.labelCell}>מכירות אשראי</Table.Th>
              <Table.Td>{asCurrency(paymentByMethod.credit)}</Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Th className={classes.labelCell}>מכירות מזומן</Table.Th>
              <Table.Td>{asCurrency(paymentByMethod.cash)}</Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Th className={classes.labelCell}>מכירות תן ביס</Table.Th>
              <Table.Td>{asCurrency(paymentByMethod.tenbis)}</Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Th className={classes.labelCell}>מכירות חשבון בית</Table.Th>
              <Table.Td>{asCurrency(paymentByMethod.houseAccount)}</Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Th className={classes.labelCell}>סה"כ מכירות</Table.Th>
              <Table.Td>
                {asCurrency(paymentByMethod.cash + paymentByMethod.credit + paymentByMethod.tenbis + paymentByMethod.houseAccount)}
              </Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Th className={classes.labelCell}>טיפ אשראי</Table.Th>
              <Table.Td>{asCurrency(paymentByMethod.tipsCredit)}</Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Th className={classes.labelCell}>טיפ מזומן</Table.Th>
              <Table.Td>{asCurrency(paymentByMethod.tipsCash)}</Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Th className={classes.labelCell}>סה"כ טיפ</Table.Th>
              <Table.Td>
                {asCurrency(paymentByMethod.tipsCash + paymentByMethod.tipsCredit + paymentByMethod.tipsTenbis + paymentByMethod.tipsHouseAccount)}
              </Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Th className={classes.labelCell}>פתיחת קופה</Table.Th>
              <Table.Td>{asCurrency(singleSessionOpeningCash)}</Table.Td>
            </Table.Tr>
            {showCashMoves && (
              <Table.Tr>
                <Table.Th className={classes.labelCell}>תנועות מזומן</Table.Th>
                <Table.Td>{asCurrency(singleSessionCashMoves)}</Table.Td>
              </Table.Tr>
            )}
            <Table.Tr className={classes.totalRow}>
              <Table.Th className={classes.labelCell}>מזומן להפקדה</Table.Th>
              <Table.Td>
                {asCurrency(paymentByMethod.cash - paymentByMethod.tipsCredit - paymentByMethod.tipsTenbis - paymentByMethod.tipsHouseAccount + singleSessionCashMoves)}
              </Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
      )}
    </SectionCard>
  );
};
